import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import employeesService from '../services/employeesService';
import { useNotification } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';
import '../styles/general.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    parseFloat(value) || 0
  );

/**
 * Read-only employee file.
 *
 * The compensation, contact and emergency-contact blocks render only when the
 * payload actually contains them. The backend withholds those fields from
 * non-staff callers, so this component reflects what it received rather than
 * deciding on its own what to hide — the access rule lives server-side.
 */
const EmployeeDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { isAdmin } = useContext(AuthContext);

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadEmployee = useCallback(async () => {
    try {
      const res = await employeesService.getEmployeeById(id);
      setEmployee(res.data);
    } catch (err) {
      console.error('Error loading employee:', err);
      notify.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, notify, t]);

  useEffect(() => {
    loadEmployee();
  }, [loadEmployee]);

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (!employee) {
    return (
      <div className="page-container-wide">
        <div className="empty-state">
          <p>{t('employees.noEmployees')}</p>
        </div>
        <button onClick={() => navigate('/employees')} className="btn btn-secondary">
          {t('employees.backToList')}
        </button>
      </div>
    );
  }

  // 'daily_salary' is present only in the staff serializer's output.
  const hasRestrictedFields = employee.daily_salary !== undefined;
  const emergency = employee.emergency_contact || {};

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('employees.detailTitle')} — {employee.full_name}</h1>
        <span className="tag-badge">
          {/* Not common.active: that key reads "Activa" (feminine, for
              "cotización"), which is wrong for "empleado" and clashes with
              the masculine inactiveBadge. */}
          {employee.is_active ? t('employees.activeBadge') : t('employees.inactiveBadge')}
        </span>
      </div>

      <fieldset className="form-fieldset">
        <legend>{t('employees.identitySection')}</legend>
        <div className="grid-3">
          <div className="section">
            <div className="section-header">{t('table.fullName')}</div>
            <div className="section-value">{employee.full_name}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('employees.nationalId')}</div>
            <div className="section-value">{employee.national_id}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('employees.taxId')}</div>
            <div className="section-value">{employee.tax_id || '—'}</div>
          </div>
        </div>
        <div className="grid-3">
          <div className="section">
            <div className="section-header">{t('employees.socialSecurityNumber')}</div>
            <div className="section-value">{employee.social_security_number}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('table.role')}</div>
            <div className="section-value">{employee.role_name}</div>
          </div>
          {employee.birth_date !== undefined && (
            <div className="section">
              <div className="section-header">{t('employees.birthDate')}</div>
              <div className="section-value">{employee.birth_date}</div>
            </div>
          )}
        </div>
      </fieldset>

      {hasRestrictedFields && (
        <fieldset className="form-fieldset">
          <legend>{t('employees.contactSection')}</legend>
          <div className="grid-3">
            <div className="section">
              <div className="section-header">{t('employees.phone')}</div>
              <div className="section-value">{employee.phone}</div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.internalPhone')}</div>
              <div className="section-value">{employee.internal_phone || '—'}</div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.address')}</div>
              <div className="section-value">{employee.address}</div>
            </div>
          </div>
        </fieldset>
      )}

      {hasRestrictedFields && (
        <fieldset className="form-fieldset">
          <legend>{t('employees.employmentSection')}</legend>
          <div className="grid-3">
            <div className="section">
              <div className="section-header">{t('employees.contractType')}</div>
              <div className="section-value">
                {t(`employees.contractTypeOptions.${employee.contract_type}`)}
              </div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.hireDate')}</div>
              <div className="section-value">{employee.hire_date}</div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.contractEndDate')}</div>
              <div className="section-value">{employee.contract_end_date || '—'}</div>
            </div>
          </div>
        </fieldset>
      )}

      {hasRestrictedFields && (
        <fieldset className="form-fieldset">
          <legend>{t('employees.compensationSection')}</legend>
          <div className="grid-2">
            <div className="section">
              <div className="section-header">{t('employees.dailySalary')}</div>
              <div className="section-value">{formatCurrency(employee.daily_salary)}</div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.payFrequency')}</div>
              <div className="section-value">
                {t(`employees.payFrequencyOptions.${employee.pay_frequency}`)}
              </div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.bankName')}</div>
              <div className="section-value">{employee.bank_name || '—'}</div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.bankAccountNumber')}</div>
              <div className="section-value">{employee.bank_account_number || '—'}</div>
            </div>
          </div>
        </fieldset>
      )}

      {hasRestrictedFields && (
        <fieldset className="form-fieldset">
          <legend>{t('employees.emergencyContactSection')}</legend>
          <div className="grid-3">
            <div className="section">
              <div className="section-header">{t('employees.emergencyName')}</div>
              <div className="section-value">{emergency.name || '—'}</div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.emergencyPhone')}</div>
              <div className="section-value">{emergency.phone || '—'}</div>
            </div>
            <div className="section">
              <div className="section-header">{t('employees.emergencyRelationship')}</div>
              <div className="section-value">{emergency.relationship || '—'}</div>
            </div>
          </div>
        </fieldset>
      )}

      {!hasRestrictedFields && (
        <p className="alert alert-warning">{t('employees.restrictedNotice')}</p>
      )}

      {!employee.is_active && (
        <p className="alert alert-warning">{t('employees.inactiveReadOnly')}</p>
      )}

      <div className="form-actions">
        {isAdmin && employee.is_active && (
          <button
            onClick={() => navigate(`/employees/${employee.id}/edit`)}
            className="btn btn-primary"
          >
            {t('common.edit')}
          </button>
        )}
        <button onClick={() => navigate('/employees')} className="btn btn-secondary">
          {t('employees.backToList')}
        </button>
      </div>
    </div>
  );
};

export default EmployeeDetailPage;
